import { format, parseISO } from "date-fns";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { DayPicker } from "@daypicker/react";
import InputMask from "@mona-health/react-input-mask";
import { filterCapacityDate, getFurthestAllowedOppositeDate } from "../../../../capacity/validation";
import useSearchState from "../../../useSearchState";
import { formatInput, inputToDate, isInputValidDate } from "../range/inputs";
import Immutable from "immutable";
import { datePickerYearsType } from "../../../../common/types/search/search";
import { DayPickerHeader } from "./components/DayPickerHeader";
import { DayPickerDropdown } from "./components/DayPickerDropdown";
import "@daypicker/react/style.css";
import { eventInputType } from "../../../../common/types/common";

interface DatePickerWrapperProps {
	id: string;
	filterDate?: (
		input: unknown,
		oppositeInput?: unknown,
		inputSelector?: unknown,
	) => unknown;
	onCriterionInputChange: (newInput: Immutable.Map<string, unknown>) => void;
	onInputChange: (inputDate: string) => void;
	value: string;
	years: datePickerYearsType;
	criterionKey?: string;
}

export const DatePickerWrapper = ({
	id,
	filterDate,
	onInputChange,
	onCriterionInputChange,
	value,
	years,
	criterionKey,
}: DatePickerWrapperProps) => {
	const { currentInput } = useSearchState(criterionKey ? [criterionKey] : null);
	const selected = value ? parseISO(value) : "";
	const [input, setInput] = useState(formatInput(selected));

	const currentSelector = id.endsWith("_from") ? "min" : "max";
	const oppositeSelector = id.endsWith("_from") ? "max" : "min";

	useEffect(() => {
		if (value === "") {
			setInput(value);
		} else if (formatInput(selected) !== input) {
			setInput(formatInput(selected));
		}
	}, [value]);

	const getOppositeInput = () =>
		currentInput?.getIn([criterionKey, oppositeSelector]);

	const isDateAvailable = (date: Date) => {
		const oppositeInput = getOppositeInput();
		if (!oppositeInput) {
			return true;
		}
		const currentDate = moment(date);
		const oppositeDate = moment(oppositeInput);

		return oppositeSelector === "max"
			? currentDate.isSameOrBefore(oppositeDate)
			: currentDate.isSameOrAfter(oppositeDate);
	};

	const filterYears = (years: datePickerYearsType) => {
		const oppositeInput = getOppositeInput();
		if (!oppositeInput) {
			return years;
		}
		const oppositeYear = new Date(oppositeInput).getFullYear();

		return years.filter(({ key }) =>
			oppositeSelector === "max"
				? key <= oppositeYear
				: key >= oppositeYear,
		);
	};

	const checkDateIsAvailable = (date: Date) => {
		const dateYear = date.getFullYear();
		const lowestYear = years[0]?.key || dateYear;
		const highestYear = years[years.length - 1]?.key || dateYear;

		const isAvailable =
			isDateAvailable(date) &&
			dateYear >= lowestYear &&
			dateYear <= highestYear;

		if (filterDate) {
			return !filterDate(date) || !isAvailable;
		}

		return !isAvailable;
	};

	const handleChange = (e: eventInputType) => {
		const input = e.target.value.toUpperCase();

		setInput(input);

		if (!isInputValidDate(input) || input.length !== 9) {
			return;
		}

		const inputDate = inputToDate(input);

		if (!isDateAvailable(inputDate)) {
			const oppositeInput = currentInput?.getIn([
				criterionKey,
				oppositeSelector,
			]);
			applyDateChange(new Date(oppositeInput));
			return;
		}

		const lowestYear = years[0]?.key;
		const inputDateYear = inputDate.getFullYear();

		if (inputDateYear < lowestYear) {
			applyDateChange(new Date(`${lowestYear}-01-01`));
			return;
		}

		const highestYear = years[years.length - 1]?.key;

		if (inputDateYear > highestYear) {
			applyDateChange(new Date(`${highestYear}-12-31`));
			return;
		}

		if (
			filterDate &&
			filterDate === filterCapacityDate &&
			!filterDate(inputDate, getOppositeInput(), oppositeSelector)
		) {
			const oppositeDate = new Date(
				getFurthestAllowedOppositeDate(inputDate, oppositeSelector),
			);
			const newInput = Immutable.Map({
				[currentSelector]: format(inputDate, "yyyy-MM-dd"),
				[oppositeSelector]: format(oppositeDate, "yyyy-MM-dd"),
			});
			onCriterionInputChange(newInput);
			return;
		}

		onInputChange(format(inputDate, "yyyy-MM-dd"));
	};

	const applyDateChange = (date: Date) => {
		// If selected date is out of range
		// change opposite date to match the range
		if (
			filterDate &&
			filterDate === filterCapacityDate &&
			!filterDate(date, getOppositeInput(), oppositeSelector)
		) {
			const oppositeDate = new Date(
				getFurthestAllowedOppositeDate(date, oppositeSelector),
			);
			const newInput = Immutable.Map({
				[currentSelector]: format(date, "yyyy-MM-dd"),
				[oppositeSelector]: format(oppositeDate, "yyyy-MM-dd"),
			});
			onCriterionInputChange(newInput);
			return;
		}

		onInputChange(format(date, "yyyy-MM-dd"));
	};

	const handleBlur = () => {
		if (!isInputValidDate(input)) {
			setInput(formatInput(selected));
		}
	};

	const availableYears = filterYears(years);

	const minYear =
		availableYears[0]?.key ||
		(selected !== "" ? selected.getFullYear() : new Date().getFullYear());
	const maxYear =
		availableYears[availableYears.length - 1]?.key ||
		(selected !== "" ? selected.getFullYear() : new Date().getFullYear());

	const selectedDate = selected === "" ? new Date() : selected;

	return (
		<>
			<InputMask
				type="text"
				id={id}
				name={id}
				value={input}
				mask="99aaa9999"
				maskPlaceholder=""
				onChange={handleChange}
				onBlur={handleBlur}
				data-criterion
			/>
			<DayPicker
				animate={false}
				captionLayout="dropdown"
				components={{
					Nav: DayPickerHeader,
					Dropdown: DayPickerDropdown,
				}}
				defaultMonth={selectedDate}
				disabled={checkDateIsAvailable}
				fixedWeeks
				endMonth={new Date(maxYear, 11)}
				mode="single"
				onSelect={applyDateChange}
				required
				selected={selectedDate}
				showOutsideDays
				startMonth={new Date(minYear, 0)}
				weekStartsOn={1}
			/>
		</>
	);
};
